import React, { useEffect, useRef, useState } from 'react';
import { ConvertStringToReact } from '../../components/ConvertStringToReact';
import { AlertBanner } from '../../components/AlertBanner';
import { Checkbox } from '../../components/Checkbox';
import { gtagEnvelope } from '../../util/gtagEnvelope';
import styles from './Trial.module.css';
import { Formik, Form, Field, FormikProps } from 'formik';
import { TrustBadge } from '~/common/components/TrustBadge';
import { InfoBanner } from '~/common/components/InfoBanner';
import { SendAsyncPostObject, TrialViewProps } from '~/common/types';
import { BusinessInput } from './BusinessInput';
import { validateEmail } from '~/common/util/customValidation';
import { businessEmailCheck } from '~/common/util/businessEmail';
import { BusinessBuyInput } from './BusinessBuyInput';
import { UserIcon } from '~/common/components/SvgIcons';
import { MSPInput } from './MSPInput';
import { FormikEffect } from '~/common/components/FormikEffect';
import { handleStandardTrialSubmission } from '~/common/util/submissions/trialSubmission';
import { handleCompanyTrialSubmission } from '~/common/util/submissions/companySubmission';
import { getErrorMessage } from '~/common/util/getErrorMessage';
import { handleMSPTrialSubmission } from '~/common/util/submissions/mspSubmission';
import { EmailOnlyInput } from './EmailOnlyInput';
import { TRIAL_TYPES } from '~/common/consts';
import { handleTeamsTrialSubmission } from '~/common/util/submissions/teamsSubmission';
import { handlePremiumTrialSubmission } from '~/common/util/submissions/premiumSubmission';
import { handleFamiliesTrialSubmission } from '~/common/util/submissions/familiesSubmission';
import { emailCheck } from '~/common/util/emailCheck';
import { postSegmentService, postTrialInterestBoomiService } from '~/common/services/trackingServices';
import DOMPurify from 'dompurify';
import { getEventName, getEventReason } from '~/common/util/getExperimentVersion';
import { TrialButtonSwitch } from './TrialButtonSwitch';

export const TrialView = (props: TrialViewProps) => {
  const {
    initialValues,
    directbuy,
    formError,
    formErrorCode,
    data,
    trialAllowed,
    trackError,
    setErrorMessage,
    setErrorCode,
    existingUserEmail,
    is2Step,
    handleCallback,
    modalContent,
  } = props;
  const { trialLength, isCompany, trialType } = window.LPFormData;
  const modalType = window.TrialForm?.modalType;
  //local state
  const [showHint, setShowHint] = useState<boolean>(false);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
  const [contactPermission, setContactPermission] = useState<boolean>(true);
  const [isBannerClosed, setIsBannerClosed] = useState<boolean>(false);
  const [marketingOptOutSeen, setMarketingOptOutSeen] = useState<boolean>(false);
  const [displayUnsubscribeCheckbox, setDisplayUnsubscribeCheckbox] = useState<boolean>(false);
  const [existingAccount, setExistingAccount] = useState<boolean>(false);
  const [mistypedEmail, setMistypedEmail] = useState<string>('');
  const [step, setStep] = useState<number>(1);

  const formikRef = useRef<FormikProps<SendAsyncPostObject>>(null);

  useEffect(() => {
    if (existingAccount || mistypedEmail) {
      formikRef.current?.setFieldError('email', 'error');
    }
  }, [existingAccount, mistypedEmail]);

  const setClosedBanner = (value: boolean) => {
    if (value !== isBannerClosed) {
      setIsBannerClosed(value);
    }
  };

  const trackErrorBlur = (id: string, validation: (value: string | number) => string | boolean, value: string) => {
    const trimmed = value ? value.trim() : value;
    formikRef.current?.setFieldValue(id, trimmed);
    formikRef.current?.setFieldError(id, undefined);
    const isInvalid = validation(trimmed);
    setTimeout(() => {
      formikRef.current?.setFieldTouched(id, true);
      if (isInvalid) {
        formikRef.current?.setFieldError(id, 'error');
        gtagEnvelope('event', 'trial_page_error', {
          error_type: 'form_field_error',
          error_field: id,
          validation_failure_reason: isInvalid,
        });
      } else {
        gtagEnvelope('event', 'trial_field_completion', {
          form_field: id,
        });
      }
    }, 300);
    if (isInvalid) {
      return false;
    }
  };

  const validateBusinessEmail = async (email: string) => {
    setExistingAccount(false);
    formikRef.current?.setFieldTouched('email', true);
    setShowHint(false);
    const invalid = validateEmail(email);
    if (invalid) {
      formikRef.current?.setFieldError('email', 'error');
      return false;
    }
    const business = await businessEmailCheck(email.toLowerCase());
    if (!business) {
      setShowHint(true);
    }
    return false;
  };

  const setOptOutOfEmails = () => {
    gtagEnvelope('event', 'cta_click', {
      cta_name: 'Email opt-out',
      cta_position: 'Trial Form',
    });
    setContactPermission(!contactPermission);
  };

  const unsubscribeClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'SPAN') {
      setMarketingOptOutSeen(true);
      gtagEnvelope('event', 'cta_click', {
        cta_name: 'Unsubscribe',
        cta_position: 'Trial Form',
      });
      setDisplayUnsubscribeCheckbox(!displayUnsubscribeCheckbox);
    }
  };

  const handleSub = async (values: SendAsyncPostObject) => {
    try {
      setFormSubmitting(true);
      setIsBannerClosed(false);
      setErrorMessage(null);
      setErrorCode(null);

      let submitResponse;

      if (isCompany) {
        submitResponse = await handleCompanyTrialSubmission({
          data,
        });
      } else if (trialType === TRIAL_TYPES.MSP) {
        submitResponse = await handleMSPTrialSubmission({
          data,
          values,
          marketingOptOutSeen,
          contactPermission,
        });
      } else if (trialType === TRIAL_TYPES.TEAMS) {
        submitResponse = await handleTeamsTrialSubmission({
          data,
          existingUserEmail,
          values,
          marketingOptOutSeen,
          contactPermission,
        });
      } else if (trialType === TRIAL_TYPES.PREMIUM) {
        submitResponse = await handlePremiumTrialSubmission({
          data,
          values,
          marketingOptOutSeen,
          contactPermission,
        });
        if (submitResponse && submitResponse.code === 1002 && submitResponse.correctedEmail) {
          setMistypedEmail(submitResponse.correctedEmail);
        }
      } else if (trialType === TRIAL_TYPES.FAMILIES) {
        submitResponse = await handleFamiliesTrialSubmission({
          data,
          existingUserEmail,
          values,
          marketingOptOutSeen,
          contactPermission,
        });
        if (submitResponse && submitResponse.code === 1002 && submitResponse.correctedEmail) {
          setMistypedEmail(submitResponse.correctedEmail);
        }
      } else {
        submitResponse = await handleStandardTrialSubmission({
          data,
          existingUserEmail,
          values,
          marketingOptOutSeen,
          contactPermission,
        });
      }
      if (submitResponse && !submitResponse.success) {
        if (submitResponse.code === 2006) {
          setExistingAccount(true);
        }

        setErrorMessage(getErrorMessage(data, submitResponse.code, existingUserEmail) || data.globalError);
        trackError({
          code: submitResponse.code,
          message: submitResponse.errorMessage || getErrorMessage(data, submitResponse.code) || data.globalError,
          step,
        });
        setFormSubmitting(false);
      }
    } catch (error) {
      setErrorMessage(data.globalError);
      trackError({
        code: '500',
        message: `Trial submission: ${error.message}`,
        runtime: true,
      });
      setFormSubmitting(false);
    }
  };

  const goToNextStep = async () => {
    gtagEnvelope('event', 'cta_click', {
      cta_name: 'Next step',
      cta_position: 'Trial Form',
    });
    setFormSubmitting(true);
    setErrorMessage(null);
    setIsBannerClosed(false);
    try {
      if (!formikRef.current?.values?.email) {
        return;
      }
      await postSegmentService({
        event: `${getEventName(true)}_trial_form_click`,
        properties: {
          reason: getEventReason(),
          journey_step: 'Step 1',
        },
      });
      const formEmail = DOMPurify.sanitize(formikRef.current.values.email.toString());
      const bossInvalid = await emailCheck(formEmail);

      if (!bossInvalid.success) {
        if (bossInvalid.code === 2006) {
          setExistingAccount(true);
        }
        setErrorMessage(getErrorMessage(data, bossInvalid.code) || data.globalError);
        trackError({
          code: bossInvalid.code,
          message: bossInvalid.errorMessage || getErrorMessage(data, bossInvalid.code) || data.globalError,
        });
      } else {
        setExistingAccount(false);
        setStep(2);
        postTrialInterestBoomiService({
          email: formEmail,
          contactPermission: contactPermission,
          trialSku: data.sku,
          programName: data.inputs?.programName?.defaultValue,
          programID: data.inputs?.programID?.defaultValue,
        });
        await postSegmentService({
          event: `${getEventName(true)}_trial_form_shown`,
          properties: {
            reason: getEventReason(),
            journey_step: 'Step 2',
          },
        });
      }
    } catch (error) {
      setErrorMessage(data.globalError);
      trackError({
        code: '500',
        message: `Trial submission: ${error.message}`,
        runtime: true,
      });
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleBackClick = async () => {
    await postSegmentService({
      event: `${getEventName(true)}_trial_form_shown`,
      properties: {
        reason: getEventReason(),
        journey_step: 'Step 1',
      },
    });
    setStep(1);
  };

  // let the modal know which step its on
  useEffect(() => {
    if (handleCallback) {
      handleCallback(step);
    }
  }, [step]);

  return (
    <>
      <h2 className={styles.header}>{step === 1 ? data.title : data.title2}</h2>
      <AlertBanner closedBanner={formError !== null || isBannerClosed} setClosedBanner={setClosedBanner} type={'error'}>
        <ConvertStringToReact htmlString={formError || data.globalError} errorCode={formErrorCode} />
      </AlertBanner>
      {isCompany && trialAllowed && (
        <p className={styles.differentEmail}>
          <ConvertStringToReact htmlString={data.useDifferentEmail.replace('{email}', `<b>${existingUserEmail}</b>`)} />
        </p>
      )}
      {step !== 1 || modalType ? (
        <></>
      ) : existingUserEmail ? (
        <div className={styles.user}>
          <div className={styles.userInfo}>
            <div className={styles.userIcon}>
              <UserIcon />
            </div>{' '}
            {existingUserEmail}
          </div>
          <a href={data.logoutLink}>{data.logoutText}</a>
        </div>
      ) : (
        <div className={styles.login}>
          <ConvertStringToReact htmlString={data.footerText} />
        </div>
      )}
      {trialAllowed && (
        <Formik<SendAsyncPostObject>
          validateOnMount={true}
          validateOnChange={true}
          validateOnBlur={true}
          innerRef={formikRef}
          initialValues={initialValues}
          onSubmit={async (values) => {
            await handleSub(values);
          }}
        >
          {({ errors, touched, values, isSubmitting, isValidating, isValid, submitCount }) => {
            return (
              <Form>
                <FormikEffect
                  submitCount={submitCount}
                  isValidating={isValidating}
                  isValid={isValid}
                  errors={errors}
                  isSubmitting={isSubmitting}
                  setClosedBanner={setClosedBanner}
                  formikRef={formikRef}
                  successUrl={data.successUrl}
                  trialLength={trialLength}
                  emailStep={step === 1 && is2Step}
                />
                {!trialAllowed || isCompany ? (
                  <></>
                ) : directbuy ? (
                  <>
                    <BusinessBuyInput
                      formikTouched={touched}
                      formikErrors={errors}
                      formikValues={values}
                      inputs={data.inputs}
                      showHint={showHint}
                      trackErrorBlur={trackErrorBlur}
                      validateBusinessEmail={validateBusinessEmail}
                      existingAccount={existingAccount}
                      userEmail={existingUserEmail}
                      existingEmailError={data.existingEmailError}
                    />
                    {!existingUserEmail && <InfoBanner text={data.passwordInfo} />}
                  </>
                ) : trialType === TRIAL_TYPES.MSP ? (
                  <MSPInput
                    formikRef={formikRef}
                    formikTouched={touched}
                    formikErrors={errors}
                    formikValues={values}
                    inputs={data.inputs}
                    showHint={showHint}
                    trackErrorBlur={trackErrorBlur}
                    validateBusinessEmail={validateBusinessEmail}
                    existingAccount={existingAccount}
                    userEmail={existingUserEmail}
                    existingEmailError={data.existingEmailError}
                  />
                ) : trialType === TRIAL_TYPES.TEAMS ||
                  trialType === TRIAL_TYPES.PREMIUM ||
                  trialType === TRIAL_TYPES.FAMILIES ? (
                  <EmailOnlyInput
                    formikRef={formikRef}
                    formikTouched={touched}
                    formikErrors={errors}
                    formikValues={values}
                    inputs={data.inputs}
                    showHint={showHint}
                    trackErrorBlur={trackErrorBlur}
                    validateBusinessEmail={validateBusinessEmail}
                    existingAccount={existingAccount}
                    userEmail={existingUserEmail}
                    existingEmailError={data.existingEmailError}
                    mistypedEmailError={mistypedEmail}
                  />
                ) : (
                  <BusinessInput
                    formikTouched={touched}
                    formikErrors={errors}
                    formikValues={values}
                    inputs={data.inputs}
                    showHint={showHint}
                    trackErrorBlur={trackErrorBlur}
                    validateBusinessEmail={validateBusinessEmail}
                    existingAccount={existingAccount}
                    userEmail={existingUserEmail}
                    existingEmailError={data.existingEmailError}
                    step={step}
                  />
                )}
                <TrialButtonSwitch
                  formSubmitting={formSubmitting}
                  data={data}
                  modalContent={modalContent}
                  handleCallback={handleCallback}
                  handleBackClick={handleBackClick}
                  step={step}
                  is2Step={is2Step}
                  goToNextStep={goToNextStep}
                />
                {step === 1 && (
                  <div onClick={unsubscribeClick} className={styles.terms}>
                    <p style={{ textAlign: modalType ? 'left' : 'center' }}>
                      <ConvertStringToReact htmlString={data!.terms} />
                    </p>
                    {displayUnsubscribeCheckbox && (
                      <Field
                        name="optOutOfEmails"
                        as={Checkbox}
                        checked={!contactPermission}
                        value={contactPermission}
                        label={data!.optOutOfEmails![0].label}
                        id="optOutOfEmails"
                        onClick={() => {
                          setOptOutOfEmails();
                        }}
                      />
                    )}
                    {!modalType && <TrustBadge />}
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
};
